import { useCallback } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

/**
 * The workspace an organization has with one service type (Compute, Kubernetes, ...): the thing a console is
 * opened on. Each service package describes its own through a `WorkspaceService`; the app merges them
 * (`src/app/workspace-services.ts`) and everything that has to know "which workspace is this organization's" reads
 * the merged list through `useOrgWorkspaces` and `resolveOrganizationWorkspace`, never from a service's own API.
 */
export type WorkspaceScope = 'org' | 'projects' | 'resources';

export interface OrgWorkspace {
  serviceType: string;
  organizationId: string;
  /** The label the service gives the workspace (the organization's name as that service knows it). */
  organizationName: string;
  /** The person's own workspace with this service, as opposed to one another organization shared with them. */
  home: boolean;
  /**
   * What the service's URLs carry to name the workspace (Compute: its metadata id). `null` when the service has no
   * workspace in the URL (Kubernetes); an empty string is a workspace that cannot be opened.
   */
  ref: string | null;
  /** The whole organization (an organization-level role), some of its projects, or single resources only. */
  scope: WorkspaceScope;
  projectIds: string[];
  /** Machines (Compute) or clusters (Kubernetes) shared one by one. */
  resourceIds: string[];
}

/** Just enough of an HTTP client for an adapter: the app's clients satisfy it, a test passes a fake. */
export interface WorkspaceClient {
  get: (url: string) => Promise<{ data: unknown }>;
}

interface WorkspaceSource<T> {
  queryKey: readonly unknown[];
  /** Passed to the query as it is: a source that shares a key with a hook takes the hook's options, so the key has one set. */
  retryOnMount?: boolean;
  fetch: () => Promise<T>;
  staleTime: number;
}

/** What a service's adapter reads out of a URL that is inside one of its workspaces. */
export interface RouteMatch {
  /** The organization whose workspace the URL names. */
  organizationId: string;
  /** The first segment under the workspace ('' = its dashboard); one of the adapter's `pages` or not. */
  page: string;
  /** The URL goes deeper than the page's list (an instance id, a create form). */
  detail: boolean;
}

/**
 * One service type's adapter: plain data and async functions (no hooks), so a package can return it from its module
 * factory like `serviceScopes`. The two answers it reads are queries of their own so that an adapter can use the key
 * of a query the console already runs and cost no second fetch.
 */
export interface WorkspaceService {
  type: string;
  home: WorkspaceSource<unknown>;
  shared: WorkspaceSource<unknown>;
  homeEntries: (raw: unknown) => OrgWorkspace[];
  sharedEntries: (raw: unknown) => OrgWorkspace[];
  /** Where `page` of this workspace's console lives. */
  href: (workspace: OrgWorkspace, page: string) => string;
  /**
   * Which workspace a location is in, from the service's workspaces (`known`); `null` for a location of another
   * service, or for a workspace nobody knows. `loc` is the path plus its search (`splitLoc`): Compute names the
   * workspace in the path and ignores the search, Kubernetes names it in the search.
   */
  matchRoute: (loc: string, known: readonly OrgWorkspace[]) => RouteMatch | null;
  /** The page slugs that exist in every workspace of the service; any other page of a switch goes to the dashboard. */
  pages: readonly string[];
  /** The workspace's first page. */
  dashboard: (workspace: OrgWorkspace) => string;
  /** The service's landing, outside any workspace. */
  landing: string;
  /** The top bar's project choice may move this service's pages (its `matchRoute` is implemented). */
  switchable: boolean;
  /** The name the service's own navigation gives a page slug ('' = dashboard); undefined for a slug it does not know. */
  pageLabel?: (page: string) => string | undefined;
}

/** Types an adapter's two raw answers; the registry holds them as `WorkspaceService`. */
export function defineWorkspaceService<H, S>(def: {
  type: string;
  home: WorkspaceSource<H>;
  shared: WorkspaceSource<S>;
  homeEntries: (raw: H) => OrgWorkspace[];
  sharedEntries: (raw: S) => OrgWorkspace[];
  href: (workspace: OrgWorkspace, page: string) => string;
  matchRoute: WorkspaceService['matchRoute'];
  pages: readonly string[];
  dashboard: (workspace: OrgWorkspace) => string;
  landing: string;
  switchable: boolean;
  pageLabel?: WorkspaceService['pageLabel'];
}): WorkspaceService {
  return {
    ...def,
    home: def.home as WorkspaceSource<unknown>,
    shared: def.shared as WorkspaceSource<unknown>,
    homeEntries: (raw) => def.homeEntries(raw as H),
    sharedEntries: (raw) => def.sharedEntries(raw as S),
  };
}

/** A location as the registry reads it: the path and the search (`?a=b`, or '') of `pathname + search`; a hash is dropped. */
export function splitLoc(loc: string): { pathname: string; search: string } {
  const [beforeHash] = loc.split('#');
  const q = beforeHash.indexOf('?');
  return q < 0 ? { pathname: beforeHash, search: '' } : { pathname: beforeHash.slice(0, q), search: beforeHash.slice(q) };
}

/** The one trim rule for an organization or workspace id: ids arrive as numbers, strings and padded strings. */
export function normalizeId(id: string | number | null | undefined): string {
  return id == null ? '' : String(id).trim();
}

/** Two ids name the same thing. An empty id names nothing, so it equals nothing. */
export function sameId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const x = normalizeId(a);
  return x !== '' && x === normalizeId(b);
}

type WorkspaceResolution<W = OrgWorkspace> = { kind: 'home' } | { kind: 'shared'; workspace: W } | { kind: 'none' };

type Resolvable = Pick<OrgWorkspace, 'serviceType' | 'organizationId' | 'home' | 'ref'>;

/**
 * Whose workspace of `serviceType` a thing of `organizationId` opens in. The same organization, or one that is not
 * named, is the person's home workspace; another organization is the workspace it shared, and never the home one
 * (that would show the wrong organization's data under a foreign URL): `none` when it shared none, or one that cannot
 * be opened (an empty `ref`; `null` is fine, the service has no workspace in its URLs).
 */
export function resolveOrganizationWorkspace<W extends Resolvable>(input: {
  serviceType: string;
  organizationId: string | number | null | undefined;
  homeOrganizationId: string | number | null | undefined;
  workspaces: readonly W[];
}): WorkspaceResolution<W> {
  const organization = normalizeId(input.organizationId);
  const home = normalizeId(input.homeOrganizationId);
  if (!organization || !home || organization === home) return { kind: 'home' };
  const workspace = input.workspaces.find(
    (w) =>
      w.serviceType === input.serviceType &&
      !w.home &&
      sameId(w.organizationId, organization) &&
      (w.ref === null || normalizeId(w.ref) !== ''),
  );
  return workspace ? { kind: 'shared', workspace } : { kind: 'none' };
}

/**
 * Whether a Compute service id may be scoped to the person's home metadata record. Compute refuses (403) a pair of project and
 * metadata of different organizations, so the home record stands in only for a service that is provably the home one: the home
 * record itself, or a service record that names the home organization. A record that carries its own metadata id does not need
 * the fallback. A service with no organization, or while the home one is unknown, is never assumed to be home.
 */
export function homeMetadataAllowed(input: {
  /** The service id is the home workspace's own metadata id. */
  ownRecord: boolean;
  detailMetadataId: string | number | null | undefined;
  serviceOrganizationId: string | number | null | undefined;
  homeOrganizationId: string | number | null | undefined;
}): boolean {
  if (input.ownRecord || normalizeId(input.detailMetadataId) !== '') return true;
  return sameId(input.serviceOrganizationId, input.homeOrganizationId);
}

/**
 * The metadata id a service's calls are scoped by: its own record's, else the home one where `homeMetadataAllowed`; undefined
 * while the record loads (no fallback is registered and then re-scoped) and when there is none to use.
 */
export function scopeMetadataId(input: {
  ownRecord: boolean;
  detailPending: boolean;
  detailMetadataId: string | number | null | undefined;
  serviceOrganizationId: string | number | null | undefined;
  homeOrganizationId: string | number | null | undefined;
  homeMetadataId: string | number | null | undefined;
}): string | undefined {
  const own = normalizeId(input.detailMetadataId);
  if (own) return own;
  if (input.detailPending || !homeMetadataAllowed(input)) return undefined;
  return normalizeId(input.homeMetadataId) || undefined;
}

interface OrgWorkspacesState {
  workspaces: OrgWorkspace[];
  /** Every answer is in (a failed one counts: see `failed`). */
  settled: boolean;
  /** Service types with an answer that failed; the others' workspaces are still listed. */
  failed: string[];
}

type SourceResult = Pick<UseQueryResult<unknown>, 'data' | 'isPending' | 'isError'>;

/** The merge `useOrgWorkspaces` makes of its queries (two per service, in registry order). */
export function combineWorkspaceResults(services: readonly WorkspaceService[], results: readonly SourceResult[]): OrgWorkspacesState {
  const workspaces: OrgWorkspace[] = [];
  const failed: string[] = [];
  let settled = true;
  services.forEach((service, i) => {
    const home = results[2 * i];
    const shared = results[2 * i + 1];
    if (home?.isPending || shared?.isPending) settled = false;
    if (home?.isError || shared?.isError) failed.push(service.type);
    if (home && !home.isError && !home.isPending) workspaces.push(...service.homeEntries(home.data));
    if (shared && !shared.isError && !shared.isPending) workspaces.push(...service.sharedEntries(shared.data));
  });
  return { workspaces, settled, failed };
}

/** The workspaces the person has or reaches, for every registered service type. */
export function useOrgWorkspaces(services: readonly WorkspaceService[]): OrgWorkspacesState {
  const combine = useCallback((results: UseQueryResult<unknown>[]) => combineWorkspaceResults(services, results), [services]);
  return useQueries({
    queries: services.flatMap((s) =>
      [s.home, s.shared].map((source) => ({
        queryKey: source.queryKey,
        queryFn: source.fetch,
        staleTime: source.staleTime,
        retryOnMount: source.retryOnMount,
        retry: false,
      })),
    ),
    combine,
  });
}

type SwitchPlan = { kind: 'stay' } | { kind: 'navigate'; to: string; toList: boolean };

/** The service whose workspace `loc` (path plus search) is in, and what it says about the URL. */
export function currentWorkspaceRoute(
  registry: readonly WorkspaceService[],
  loc: string,
  workspaces: readonly OrgWorkspace[],
): { service: WorkspaceService; match: RouteMatch } | null {
  for (const service of registry) {
    const match = service.matchRoute(loc, workspaces.filter((w) => w.serviceType === service.type));
    if (match) return { service, match };
  }
  return null;
}

/**
 * What choosing a project of `choice.organizationId` does to the page that is open. Inside a service's workspace of
 * another organization it opens the target organization's workspace on the same page type; a detail page goes to its
 * list (`toList`), a page the service does not have in every workspace goes to the dashboard. It stays where it is for
 * `All projects` (`choice` null), for the workspace already open, for an organization with no workspace of that
 * service, and on any page that is not inside a workspace (Organizations, Teams, ...).
 */
export function planSwitch(input: {
  registry: readonly WorkspaceService[];
  /** The open page: path plus search (`pathname + search`). */
  loc: string;
  choice: { organizationId: string } | null;
  workspaces: readonly OrgWorkspace[];
}): SwitchPlan {
  const stay: SwitchPlan = { kind: 'stay' };
  if (!input.choice) return stay;
  const current = currentWorkspaceRoute(input.registry, input.loc, input.workspaces);
  if (!current) return stay;
  const { service, match } = current;
  if (sameId(match.organizationId, input.choice.organizationId)) return stay;
  const target = input.workspaces.find(
    (w) => w.serviceType === service.type && sameId(w.organizationId, input.choice?.organizationId) && (w.ref === null || normalizeId(w.ref) !== ''),
  );
  if (!target) return stay;
  if (match.page === '' || !service.pages.includes(match.page)) return { kind: 'navigate', to: service.dashboard(target), toList: false };
  return { kind: 'navigate', to: service.href(target, match.page), toList: match.detail };
}

/**
 * `Back to your organization`: the same page type in the home workspace; when the service has no home workspace to go
 * to (none listed, or one that cannot be opened) its landing, never a silent no-op. `stay` on a page that is not
 * inside a workspace of the registry's services.
 */
export function planBack(input: {
  registry: readonly WorkspaceService[];
  loc: string;
  workspaces: readonly OrgWorkspace[];
}): SwitchPlan {
  const current = currentWorkspaceRoute(input.registry, input.loc, input.workspaces);
  if (!current) return { kind: 'stay' };
  const home = input.workspaces.find(
    (w) => w.serviceType === current.service.type && w.home && (w.ref === null || normalizeId(w.ref) !== ''),
  );
  if (!home) return { kind: 'navigate', to: current.service.landing, toList: false };
  return planSwitch({ ...input, choice: { organizationId: home.organizationId } });
}

/**
 * What to do with a stored choice that is no longer usable (`revoked`: both project lists loaded and it is in neither).
 * `clear` drops it; `redirect` is the landing to go to when the open page is the workspace of that very choice's
 * organization (another organization's workspace, or a project of the home one, is not touched) and only when no other
 * project reaches that organization any more (`organizationReachable`): losing one project of an organization the person
 * still reaches changes the choice, not the page.
 * The choice is kept while the redirect cannot be decided yet: on a page inside a service while the workspaces are
 * loading, or while that service's workspace list failed (it is retried later rather than losing the redirect). On any
 * other page the clear depends on the project lists alone.
 */
export function planRevoked(input: {
  registry: readonly WorkspaceService[];
  loc: string;
  revoked: boolean;
  stored: { organizationId: string } | null;
  /** The person still reaches the choice's organization through another project (`organizationStillReachable`). */
  organizationReachable: boolean;
  workspaces: readonly OrgWorkspace[];
  settled: boolean;
  /** Service types whose workspace list failed (`useOrgWorkspaces().failed`). */
  failed: readonly string[];
}): { clear: boolean; redirect: string | null } {
  const wait = { clear: false, redirect: null };
  if (!input.revoked) return wait;
  const path = splitLoc(input.loc).pathname;
  const inside = input.registry.filter((s) => path.startsWith(`${s.landing}/`));
  if (inside.some((s) => input.failed.includes(s.type))) return wait;
  if (!input.settled) return { clear: inside.length === 0, redirect: null };
  const current = currentWorkspaceRoute(input.registry, input.loc, input.workspaces);
  const open = current && input.workspaces.find((w) => w.serviceType === current.service.type && sameId(w.organizationId, current.match.organizationId));
  const redirect = current && open && !open.home && !input.organizationReachable && input.stored && sameId(input.stored.organizationId, open.organizationId) ? current.service.landing : null;
  return { clear: true, redirect };
}
